import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { useForm } from "react-yup";
import * as Yup from "yup";

import { Alert, AlertProps, Form, yupLocale } from "@skv/core";
import { usePageEnvelope } from "@skv/state";
import { supportDetails } from "@skv/util";

interface ContactFormLocationState {
  type?: "volunteer" | "organisation";
  subject?: string;
  message?: string;
}

type FormValues = ContactFormLocationState & {
  email: string;
  fullName: string;
  supportDetails: string;
  organisation?: string;
  phone?: string;
};

type ContactFormViewModel = {
  data: DataModel;
  success: false;
  errorCode: string;
  errorMessage: string;
  meta: null;
};

interface DataModel {
  contactForm?: FormValues;
  formErrors?: FormErrors;
}

interface FormErrors {
  type?: string[];
  subject?: string[];
  message?: string[];
  email?: string[];
  fullName?: string[];
  organisation?: string[];
  phone?: string[];
}

export const ContactForm = () => {
  const location = useLocation();

  const { pageEnvelope } = usePageEnvelope<ContactFormViewModel>();
  const { contactForm: formData = {} as FormValues, formErrors = {} } =
    (pageEnvelope.data ?? {}) as DataModel;

  const { state } = useLocation<ContactFormLocationState>();

  const isForOrganisation = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const organisation = searchParams.get("organisation");
    return organisation === "true";
  }, [location.search]);

  Yup.setLocale(yupLocale);
  const SCHEMA = Yup.object({
    fullName: Yup.string().required().label("Full name").nullable(),
    organisation: Yup.string()
      .nullable()
      .when("type", {
        is: (type) => type === "organisation",
        then: Yup.string().required().label("Organisation"),
      }),
    email: Yup.string().email().required().label("Email").nullable(),
    subject: Yup.string().label("Subject").required().nullable(),
    message: Yup.string().label("Message").required().nullable(),
    phone: Yup.string().label("Phone number").nullable(),
    type: Yup.string().oneOf(["volunteer", "organisation"] as const),
    supportDetails: Yup.string().nullable().notRequired(),
  }).defined();

  const {
    FormProvider,
    createSubmitHandler,
    isSubmitting,
    field,
    values,
    getTouched,
    getError,
  } = useForm({
    validationSchema: SCHEMA,
    defaultValues: {
      ...(formData ?? {}),
      type:
        formData?.type ?? state?.type ?? isForOrganisation
          ? "organisation"
          : "volunteer",
      subject: formData?.subject ?? state?.subject,
      message: formData?.message ?? state?.message,
    },
    defaultTouched: touchedFromFormErrors(formErrors),
    defaultErrors: flattenFormErrors(formErrors),
  });

  const formRef = React.useRef<HTMLFormElement>(null);

  const handleSubmit = createSubmitHandler(() => {
    formRef.current.submit();
  });

  const [alertState] = React.useState<AlertProps>(() => {
    if (pageEnvelope.errorCode === "error:contact:formvalidation") {
      return {
        show: true,
        variant: "danger",
        children: (
          <>
            <span>Please fix the below errors in your form:</span>
            <ul className="mb-0">
              {Object.entries((pageEnvelope.data as DataModel).formErrors)
                .reduce((acc, cur) => {
                  acc = [...acc, ...cur[1]];
                  return acc;
                }, [])
                .map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
            </ul>
          </>
        ),
      };
    }

    if (pageEnvelope.errorCode) {
      return {
        variant: "danger",
        show: false,
        children: <>{pageEnvelope.errorMessage ?? "Something went wrong."}</>,
      };
    }

    return {
      variant: "danger",
      show: false,
      children: null,
    };
  });

  return (
    <FormProvider>
      <h2 className="mb-4">Contact Us</h2>
      <div className="row">
        <div className="col-md-9 col-lg-7">
          <section className="bg-light p-4 mb-6">
            <h3>Have a question?</h3>
            <p>Fill out this form and we'll reply in one or two days.</p>
            <p>All fields marked with * are required.</p>

            <Alert {...alertState} />

            <form ref={formRef} method="POST" onSubmit={handleSubmit}>
              <fieldset>
                <legend className="fs-6 mb-2">Are you?</legend>
              </fieldset>
              <input
                type="hidden"
                name="supportDetails"
                id="supportDetails"
                value={JSON.stringify(supportDetails())}
              />
              <div className="fw-regular mb-3">
                <Form.Check
                  name="type"
                  type="radio"
                  label="A volunteer"
                  id="contact-volunteer"
                  value="volunteer"
                  checked={values.type === "volunteer"}
                  {...field}
                />
                <Form.Check
                  name="type"
                  type="radio"
                  label="A volunteering organisation"
                  id="contact-organisation"
                  value="organisation"
                  checked={values.type === "organisation"}
                  {...field}
                />
              </div>
              <Form.Field
                id="fullName"
                name="fullName"
                label="Full name"
                autoComplete="name"
                {...field}
              />

              {values.type === "organisation" && (
                <Form.Field
                  id="organisation"
                  name="organisation"
                  label="Organisation"
                  {...field}
                />
              )}

              <Form.Field
                type="email"
                id="email"
                name="email"
                label="Email address"
                autoComplete="email"
                {...field}
              />

              <Form.PureField
                id="phone"
                name="phone"
                label="Phone number"
                autoComplete="tel"
                optionalMessage={null}
                value={values.phone ?? ""}
                error={getError("phone")}
                touched={getTouched().phone}
                {...field}
              />

              <h4>Your question</h4>
              <Form.Field
                id="subject"
                name="subject"
                label="Subject"
                {...field}
              />
              <Form.TextArea
                id="message"
                name="message"
                label="Message"
                rows={5}
                {...field}
              />

              <p className="small">
                By providing your personal information, you agree to our{" "}
                <Link to="/legal/privacy-statement">Privacy Policy</Link>.
              </p>

              <button className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send"}
              </button>

              <div
                css={`
                  position: absolute;
                  opacity: 0;
                  top: 0;
                  z-index: -1;
                `}
              >
                <Form.Field
                  tabIndex={0}
                  id="secondName"
                  name="secondName"
                  label="secondName"
                  autoComplete="off"
                  {...field}
                />
              </div>
            </form>
          </section>
        </div>
      </div>
    </FormProvider>
  );
};

function flattenFormErrors(errors: FormErrors) {
  const result: { [K in keyof FormErrors]: string } = {};

  Object.entries(errors).forEach(([key, value]) => {
    result[key as keyof FormErrors] =
      Array.isArray(value) && value.length ? value[0] : "";
  });

  return result;
}

function touchedFromFormErrors(errors: FormErrors) {
  const result: { [K in keyof FormErrors]: boolean } = {};

  Object.entries(errors).forEach(([key, value]) => {
    result[key as keyof FormErrors] = Array.isArray(value) && value.length > 0;
  });

  return result;
}
